export type EventListener<T> = (payload: T) => void;

/** A small typed event emitter that does not depend on Node.js APIs. */
export class EventEmitter<TEvents extends object> {
  private readonly listeners = new Map<string, Set<EventListener<unknown>>>();

  on<K extends keyof TEvents & string>(event: K, listener: EventListener<TEvents[K]>): () => void {
    const listeners = this.listeners.get(event) ?? new Set<EventListener<unknown>>();
    listeners.add(listener as EventListener<unknown>);
    this.listeners.set(event, listeners);
    return () => this.off(event, listener);
  }

  once<K extends keyof TEvents & string>(event: K, listener: EventListener<TEvents[K]>): () => void {
    const unsubscribe = this.on(event, (payload) => {
      unsubscribe();
      listener(payload);
    });
    return unsubscribe;
  }

  off<K extends keyof TEvents & string>(event: K, listener: EventListener<TEvents[K]>): void {
    const listeners = this.listeners.get(event);
    listeners?.delete(listener as EventListener<unknown>);
    if (listeners?.size === 0) this.listeners.delete(event);
  }

  emit<K extends keyof TEvents & string>(event: K, payload: TEvents[K]): void {
    for (const listener of [...(this.listeners.get(event) ?? [])]) {
      try {
        listener(payload);
      } catch (error) {
        queueMicrotask(() => {
          throw error;
        });
      }
    }
  }

  removeAllListeners(event?: keyof TEvents & string): void {
    if (event) this.listeners.delete(event);
    else this.listeners.clear();
  }
}

